import React from "react";
import { Box, Card, CardContent, Chip, Stack, Typography } from "@mui/joy";
import ExtensionIcon from "@mui/icons-material/Extension";
import { OrganizationIntegration } from "../../common/types";
import { IntegrationCatalogEntry } from "../../types/integrationCatalog";

const statusColor = (
  enabled: boolean,
  integration?: OrganizationIntegration,
) => {
  if (enabled) return "success";
  if (integration?.status === "inactive") return "neutral";
  return "warning";
};

interface IntegrationCardFrameProps {
  entry: IntegrationCatalogEntry;
  integration?: OrganizationIntegration;
  enabled: boolean;
  statusLabel: string;
  tierBadge: string;
  preview?: string | null;
  metadata?: React.ReactNode;
  actions: React.ReactNode;
}

const IntegrationCardFrame: React.FC<IntegrationCardFrameProps> = ({
  entry,
  integration,
  enabled,
  statusLabel,
  tierBadge,
  preview,
  metadata,
  actions,
}) => (
  <Card variant="outlined">
    <CardContent>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "flex-start" }}
          spacing={2}
        >
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ExtensionIcon color="primary" />
              <Typography level="title-lg">{entry.display_name}</Typography>
            </Stack>
            {entry.description && (
              <Typography level="body-sm" color="neutral">
                {entry.description}
              </Typography>
            )}
          </Stack>
          <Chip
            size="sm"
            variant="soft"
            color={statusColor(enabled, integration) as any}
          >
            {statusLabel}
          </Chip>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip size="sm" variant="outlined" color="neutral">
            {entry.category || "Uncategorized"}
          </Chip>
          <Chip size="sm" variant="soft" color="primary">
            {tierBadge}
          </Chip>
        </Stack>

        {(preview || metadata) && (
          <Box>
            {preview && (
              <Typography level="body-sm" color="neutral">
                {preview}
              </Typography>
            )}
            {metadata}
          </Box>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1}
        >
          <Box />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            {actions}
          </Stack>
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

export default IntegrationCardFrame;
